'use client';

/**
 * Multi-state auralization for public tools (Pure / Bare / Panel / Room…).
 * Same AuralizationEngine + samples path as the product; not limited to before/after.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuralizationEngine, type TrimWindow } from '@/lib/audio/engine';
import type { RoomAcoustics } from '@/lib/audio/types';

export interface ToolAuralizationApi {
  status: string;
  building: boolean;
  loading: boolean;
  error: string | null;
  hasData: boolean;
  ready: string[];
  isPlaying: boolean;
  activeState: string;
  elapsed: number;
  duration: number;
  sourceName: string | null;
  loadFile: (file: File) => void;
  loadSampleUrl: (url: string, name: string, trim?: TrimWindow) => void;
  togglePlay: () => void;
  setActive: (s: string) => void;
  seekTo: (seconds: number) => void;
}

const isBuildingStatus = (s: string) => /^(Building|Processing|Decoding)/.test(s);

function statesSig(states: Record<string, RoomAcoustics> | null): string {
  if (!states) return '';
  return Object.keys(states)
    .sort()
    .map((k) => {
      const m = states[k].freqResponse.mag;
      return `${k}:${m.length}:${m[0]}:${m[m.length - 1]}:${states[k].nProducts}`;
    })
    .join('|');
}

/** Pick a valid state id without setState-in-effect (React Compiler). */
function resolveActiveState(
  states: Record<string, RoomAcoustics> | null,
  preferred: string,
  defaultState?: string,
): string {
  if (!states) return preferred;
  if (preferred && states[preferred]) return preferred;
  if (defaultState && states[defaultState]) return defaultState;
  return Object.keys(states)[0] ?? preferred;
}

export function useToolAuralization(
  states: Record<string, RoomAcoustics> | null,
  defaultState?: string,
): ToolAuralizationApi {
  const engineRef = useRef<AuralizationEngine | null>(null);
  const [status, setStatus] = useState('');
  const [ready, setReady] = useState<string[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeState, setActiveStateS] = useState(defaultState ?? '');
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [sourceName, setSourceName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const sig = useMemo(() => statesSig(states), [states]);
  const resolvedActive = resolveActiveState(states, activeState, defaultState);

  useEffect(() => {
    const eng = new AuralizationEngine({
      onStatus: setStatus,
      onReady: (ids) => {
        setLoadError(null);
        setLoading(false);
        setReady(ids);
      },
      onPlayingChange: setIsPlaying,
      onProgress: (e, d) => {
        setElapsed(e);
        setDuration(d);
      },
      onEnded: () => setElapsed(0),
    });
    engineRef.current = eng;
    return () => {
      eng.dispose();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!armed || !states || !sig) return;
    const eng = engineRef.current;
    if (!eng) return;
    const ids = Object.keys(states);
    if (ids.length === 0) return;
    const active = resolveActiveState(states, activeState, defaultState);
    const t = setTimeout(() => {
      void eng.setStates(states).then(() => {
        eng.setActiveState(active);
      });
    }, 350);
    return () => clearTimeout(t);
    // intentional: only rebuild IRs when acoustics signature / arming changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, sig]);

  const loadFile = useCallback((file: File) => {
    setLoadError(null);
    setArmed(true);
    setSourceName(file.name);
    setReady([]);
    setLoading(true);
    const eng = engineRef.current;
    eng?.resume();
    eng?.loadSourceFile(file).catch(() => {
      setLoading(false);
      setLoadError('Could not decode that file — is it a valid audio file?');
    });
  }, []);

  const loadSampleUrl = useCallback((url: string, name: string, trim?: TrimWindow) => {
    setLoadError(null);
    setArmed(true);
    setSourceName(name);
    setReady([]);
    setLoading(true);
    const fileName = url.split('/').pop() ?? url;
    const eng = engineRef.current;
    eng?.resume();
    eng?.loadSourceUrl(url, trim).catch(() => {
      setLoading(false);
      setLoadError(`"${name}" isn't uploaded yet — add ${fileName} to public/audio/samples/.`);
    });
  }, []);

  const togglePlay = useCallback(() => {
    engineRef.current?.toggle(resolvedActive);
  }, [resolvedActive]);

  const setActive = useCallback((s: string) => {
    setActiveStateS(s);
    engineRef.current?.setActiveState(s);
  }, []);

  const seekTo = useCallback((seconds: number) => {
    engineRef.current?.seek(seconds);
  }, []);

  return {
    status,
    building: isBuildingStatus(status),
    loading,
    error: loadError,
    hasData: !!states && Object.keys(states).length > 0,
    ready,
    isPlaying,
    activeState: resolvedActive,
    elapsed,
    duration,
    sourceName,
    loadFile,
    loadSampleUrl,
    togglePlay,
    setActive,
    seekTo,
  };
}
